/**
 * Learning session identity.
 *
 * A "session" is a contiguous period of learning activity. Without a session
 * identifier on activity records, engagement, time-on-task and within-session
 * sequencing cannot be reconstructed — and unlike most data problems, this one
 * cannot be backfilled later. Every day without it permanently shrinks the
 * studyable history.
 *
 * Definition used here: the session ends after 30 minutes of inactivity, which
 * is the common convention in learning analytics and web analytics alike.
 * Touching the session on each recorded activity extends it.
 *
 * The identifier is random and carries no personal information. It lives in
 * sessionStorage, so it does not persist across browser restarts.
 */

const ID_KEY = 'learning_session_id';
const TOUCH_KEY = 'learning_session_last_activity';

/** Inactivity after which a new session begins. */
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function newId(): string {
  // crypto.randomUUID is unavailable on older Safari and on insecure origins.
  if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
    return (crypto as any).randomUUID();
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The current session id, starting a new session if the previous one expired.
 * Calling this counts as activity and extends the session.
 */
export function getSessionId(): string {
  let id: string | null = null;
  let last = 0;

  try {
    id = sessionStorage.getItem(ID_KEY);
    last = Number(sessionStorage.getItem(TOUCH_KEY) || 0);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Fall back to
    // a per-call id rather than breaking activity recording.
    return newId();
  }

  const now = Date.now();
  const expired = !id || !last || now - last > SESSION_TIMEOUT_MS;

  if (expired) {
    id = newId();
  }

  try {
    sessionStorage.setItem(ID_KEY, id!);
    sessionStorage.setItem(TOUCH_KEY, String(now));
  } catch {
    /* non-fatal */
  }

  return id!;
}

/** Forces a new session on the next call. Used on logout. */
export function resetSession(): void {
  try {
    sessionStorage.removeItem(ID_KEY);
    sessionStorage.removeItem(TOUCH_KEY);
  } catch {
    /* non-fatal */
  }
}
